import { Component, computed, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import {
  YdsButtonComponent,
  YdsCardComponent,
  YdsEmptyStatesComponent,
  YdsSpinnerComponent,
  YdsTableComponent,
  YdsSplitButtonComponent,
  YdsTableCellDirective,
  YdsPaginationComponent,
  type YdsTableColumn,
  type YdsSplitButtonItem,
} from '../../../yds-angular';
import { formatDisplayDate } from '../../../shared/general-ledger-yds.utils';
import {
  GeneralledgerGroupService,
  type GeneralLedgerGroup,
} from '../generalledgergroup.service';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';
import { deleteGeneralLedgerGroupWithChecks } from '../generalledgergroup-shared';

@Component({
  selector: 'app-generalledgergroup-list',
  imports: [
    YdsCardComponent,
    YdsButtonComponent,
    YdsTableComponent,
    YdsSpinnerComponent,
    YdsEmptyStatesComponent,
    YdsSplitButtonComponent,
    YdsTableCellDirective,
    YdsPaginationComponent,
  ],
  templateUrl: './generalledgergroup-list.component.html',
  styleUrl: './generalledgergroup-list.component.scss',
})
export class GeneralledgergroupListComponent implements OnInit {
  readonly pageSize = 10;
  readonly tableColumns: YdsTableColumn[] = [
    { key: 'generalLedger_Group_Name', header: 'Omschrijving' },
    { key: 'startDate', header: 'Begindatum' },
    { key: 'endDate', header: 'Einddatum' },
    { key: 'actions', header: 'Actieknop', width: '160px' },
  ];

  readonly splitButtonItems: readonly YdsSplitButtonItem[] = [
    { id: 'view', label: 'Bekijk details' },
    { id: 'edit', label: 'Wijzigen' },
    { id: 'generalledger', label: 'Grootboek' },
    { id: 'generalledgeraccount', label: 'Grootboekrekening' },
    { id: 'delete', label: 'Verwijderen' },
  ];

  private router = inject(Router);
  private groupService = inject(GeneralledgerGroupService);
  private dialogService = inject(ConfirmDialogService);
  private destroyRef = inject(DestroyRef);

  generalLedgerGroups: WritableSignal<GeneralLedgerGroup[]> = signal([]);
  pageIndex = signal(0);
  isLoading = signal(true);

  private reload$ = new Subject<void>();

  pagedGroups = computed(() => {
    const start = this.pageIndex() * this.pageSize;
    return this.generalLedgerGroups().slice(start, start + this.pageSize);
  });

  tableItems = computed(() =>
    this.pagedGroups().map((item) => ({
      id: item.id,
      generalLedger_Group_Name: item.generalLedger_Group_Name,
      startDate: formatDisplayDate(item.startDate, 'nl-NL'),
      endDate: this.formatEndDate(item.endDate),
    })),
  );

  private formatEndDate(endDate: GeneralLedgerGroup['endDate']): string {
    const formatted = formatDisplayDate(endDate, 'nl-NL');
    return formatted === '31-12-9999' ? 'n.v.t' : formatted;
  }

  constructor() {
    this.reload$
      .pipe(
        switchMap(() => {
          this.isLoading.set(true);
          this.generalLedgerGroups.set([]);
          return this.groupService.getGroupsByContext().pipe(
            catchError(() => {
              this.isLoading.set(false);
              this.generalLedgerGroups.set([]);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (items) => {
          this.generalLedgerGroups.set(items);
          this.pageIndex.set(0);
          this.isLoading.set(false);
        },
      });
  }

  ngOnInit(): void {
    this.loadGroups();
  }

  loadGroups(): void {
    this.reload$.next();
  }

  navigateToCreate(): void {
    this.router.navigate(['/config/generalledgergroup/editable']);
  }

  onTableRowClick(row: Record<string, unknown>): void {
    // Default action when clicking a row is "Bekijk details".
    this.viewItem(String(row['id']));
  }

  viewItem(id: string, event?: MouseEvent): void {
    event?.stopPropagation();
    this.router.navigate(['/config/generalledgergroup/editable', id]);
  }

  handleActionClick(id: string, actionId: string): void {
    if (actionId === 'view' || actionId === 'edit') {
      this.viewItem(id);
      return;
    }
    if (actionId === 'generalledger') {
      this.router.navigate(['/config/generalledger']);
      return;
    }
    if (actionId === 'generalledgeraccount') {
      this.router.navigate(['/config/generalledgeraccount']);
      return;
    }
    if (actionId === 'delete') {
      this.deleteItem(id);
      return;
    }
  }

  private deleteItem(id: string): void {
    deleteGeneralLedgerGroupWithChecks({
      id,
      service: this.groupService,
      dialogService: this.dialogService,
      destroyRef: this.destroyRef,
      onDeleted: () => this.loadGroups(),
    });
  }
}
